import React, { useCallback, useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { DynamicTableV2 } from '@packages/table-builder';
import { Typography } from '@packages/ui/primitives';
import { Divider } from '@packages/ui/layout';
import type { FormEmbeddedTableNode } from '../types/layout';
import { useFormEngineForm } from '../context/FormEngineContext';
import { TableSchemaRegistry } from '../resolvers/tableSchemaRegistry';
import { axiosInstance } from '@/services/apiInterceptor';

interface FormEmbeddedTableRendererProps {
  node: FormEmbeddedTableNode;
}

function resolveFilterValues(
  filter: Record<string, unknown>,
  formValues: Record<string, unknown>,
): Record<string, unknown> {
  const resolved: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(filter)) {
    if (typeof value === 'string' && value.startsWith('#/properties/')) {
      const fieldPath = value.replace(/^#\/properties\//, '').replace(/\/properties\//g, '.');
      resolved[key] = formValues[fieldPath];
    } else {
      resolved[key] = value;
    }
  }
  return resolved;
}

export const FormEmbeddedTableRenderer = React.memo(function FormEmbeddedTableRenderer({
  node,
}: FormEmbeddedTableRendererProps): React.ReactElement | null {
  const { form } = useFormEngineForm();

  const schema = TableSchemaRegistry.get(node.schemaRef);

  const watchedFields = useMemo(() => {
    if (!node.filter) return [];
    return Object.values(node.filter)
      .filter((v): v is string => typeof v === 'string' && v.startsWith('#/properties/'))
      .map((v) => v.replace(/^#\/properties\//, '').replace(/\/properties\//g, '.'));
  }, [node.filter]);

  const watchedValues = useWatch({
    control: form.control,
    name: watchedFields as never[],
    disabled: watchedFields.length === 0,
  });

  const resolvedFilter = useMemo(() => {
    if (!node.filter) return undefined;
    const formValues = form.getValues();
    return resolveFilterValues(node.filter, formValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node.filter, watchedValues]);

  const handleEditStateChange = useCallback(
    (hasEdits: boolean) => {
      form.setValue('__embeddedTableDirty', (hasEdits ? true : false) as never, { shouldDirty: hasEdits });
    },
    [form],
  );

  if (!schema) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(
        `[form-engine] embedded-table: no schema found for ref "${node.schemaRef}". ` +
        'Register it via TableSchemaRegistry.register() at app startup.',
      );
    }
    return null;
  }

  const embeddedFeatures = {
    layout: { noPadding: true },
    ...(node.features as Record<string, unknown> | undefined),
  };

  return (
    <div data-embedded-table={node.schemaRef}>
      {node.title && (
        <>
          <Divider />
          <Typography variant='label-l' style={{ padding: '12px 0 4px' }}>
            {node.title}
          </Typography>
        </>
      )}
      <DynamicTableV2
        schema={{ ...(schema as any), features: { ...(schema as any).features, ...embeddedFeatures } }}
        axiosInstance={axiosInstance as any}
        semanticId={node.semanticId ?? `embedded:${node.schemaRef}`}
        onEditStateChange={handleEditStateChange}
        {...(resolvedFilter != null && { filter: resolvedFilter })}
      />
    </div>
  );
});
